import { type Context, FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useRouter } from '@eneco/next';
import { Box, NotificationBox } from '@eneco/sparky';
import { NotificationBoxProps } from '@eneco/sparky/types';

type NotificationContent = (Omit<NotificationBoxProps, 'isAlert' | 'isElevated'> & { route?: string }) | null;

type NotificationContextType = {
  notification: NotificationContent;
  setNotification: (notification: NotificationContent) => void;
};

// Use a singleton context to prevent duplication when module is evaluated multiple times
// (e.g. due to vi.mock creating separate module evaluation graphs in Vitest)
const NOTIFICATION_CONTEXT_KEY = '__eneco_NotificationContext__';
const NotificationContext: Context<NotificationContextType | null> =
  ((globalThis as Record<string, unknown>)[NOTIFICATION_CONTEXT_KEY] as Context<NotificationContextType | null>) ||
  ((globalThis as Record<string, unknown>)[NOTIFICATION_CONTEXT_KEY] = createContext<NotificationContextType | null>(
    null,
  ));

const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be wrapped inside NotificationContextProvider');
  }
  return context;
};

export const useRedirectAndNotify = () => {
  const { pushHref } = useRouter();
  const { setNotification } = useNotification();

  return useCallback(
    (notification: NotificationContent & { route: string }) => {
      setNotification(notification);
      pushHref(notification.route);
    },
    [setNotification, pushHref],
  );
};

export const useSetNotification = () => {
  const { setNotification } = useNotification();
  return setNotification;
};

export const NotificationContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationContent>(null);

  return (
    <NotificationContext.Provider
      value={{
        notification,
        setNotification,
      }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const Notification: FC<{ children?: never }> = () => {
  const router = useRouter();
  const { notification, setNotification } = useNotification();
  const showNotificationOnCurrentRoute = !notification?.route || notification.route === router.activePath;

  useEffect(() => {
    if (!showNotificationOnCurrentRoute) return;

    const onBeforeRouteChange = () => setNotification(null);
    router.events.on('beforeHistoryChange', onBeforeRouteChange);

    return () => {
      router.events.off('beforeHistoryChange', onBeforeRouteChange);
    };
  }, [router, setNotification, showNotificationOnCurrentRoute]);

  if (!notification || !showNotificationOnCurrentRoute) return null;

  const { route, ...notificationProps } = notification;

  return (
    <Box paddingBottom="6">
      <NotificationBox isElevated={true} isAlert={true} {...notificationProps} />
    </Box>
  );
};
